import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, catchError, map, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SmartTemplateService {
  private serviceName = 'SmartTemplateService';
  private token!: string;
  private basePath = environment.interop.basePath;
  private httpOptions: any;

  // Smart Template Group endpoints
  private smartTemplateGroupsUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.groups.list;
  
  private smartTemplateGroupUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.groups.one;
  
  private createSmartTemplateGroupUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.groups.store;
  
  private updateSmartTemplateGroupUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.groups.update;
  
  private deleteSmartTemplateGroupUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.groups.delete;

  // Smart Template Parts endpoints
  private smartTemplatePartsUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parts.list;
  
  private smartTemplatePartUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parts.one;
  
  private createSmartTemplatePartUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parts.store;
  
  private updateSmartTemplatePartUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parts.update;
  
  private deleteSmartTemplatePartUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parts.delete;

  // Template Parameters endpoints
  private templateParametersUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parameters.list;
  
  private templateParameterUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parameters.one;
  
  private createTemplateParameterUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parameters.store;
  
  private updateTemplateParameterUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parameters.update;
  
  private deleteTemplateParameterUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parameters.delete;

  // Additional utility endpoints
  private categoriesUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.categories;
  
  private availablePartsUrl = 
    this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.availableParts;

  constructor(private http: HttpClient) {}

  setToken(token: string) {
    this.token = token;
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${this.token}`,
      }),
    };
  }

  // Smart Template Groups
  /**
   * Get all smart template groups
   */
  getSmartTemplateGroups(): Observable<any> {
    return this.http.get(this.smartTemplateGroupsUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get smart template group by ID with parts and parameters
   */
  getSmartTemplate(id: number): Observable<any> {
    const findUrl = this.basePath + `api/nomenclatoare/smart-templates/groups/find/${id}`;
    return this.http.get(findUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Create new smart template group with parts and parameters
   */
  createSmartTemplate(data: any): Observable<any> {
    return this.http.post(this.createSmartTemplateGroupUrl, data, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update smart template group with parts and parameters
   */
  updateSmartTemplate(id: number, data: any): Observable<any> {
    const updateUrl = this.basePath + `api/nomenclatoare/smart-templates/groups/${id}`;
    return this.http.put(updateUrl, data, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete smart template group - for management interface
   */
  deleteSmartTemplate(id: number): Observable<any> {
    const deleteUrl = this.basePath + `api/nomenclatoare/smart-templates/${id}`;
    return this.http.delete(deleteUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // Smart Template Parts
  /**
   * Get all available template parts
   */
  getAvailableParts(): Observable<any> {
    return this.http.get(this.availablePartsUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get template parts for a specific group
   */
  getTemplatePartsByGroup(groupId: number): Observable<any> {
    return this.http.get(`${this.smartTemplatePartsUrl}?group_id=${groupId}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get template part by ID
   */
  getTemplatePart(id: number): Observable<any> {
    return this.http.get(`${this.smartTemplatePartUrl}${id}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Create new template part
   */
  createTemplatePart(data: any): Observable<any> {
    return this.http.post(this.createSmartTemplatePartUrl, data, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update template part
   */
  updateTemplatePart(id: number, data: any): Observable<any> {
    return this.http.put(`${this.updateSmartTemplatePartUrl}${id}`, data, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete template part
   */
  deleteTemplatePart(id: number): Observable<any> {
    return this.http.delete(`${this.deleteSmartTemplatePartUrl}${id}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // Template Parameters
  /**
   * Get all template parameters
   */
  getTemplateParameters(): Observable<any> {
    return this.http.get(this.templateParametersUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get template parameters for a specific group
   */
  getTemplateParametersByGroup(groupId: number): Observable<any> {
    return this.http.get(`${this.templateParametersUrl}?group_id=${groupId}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Bulk update parameters
   */
  bulkUpdateParameters(data: any): Observable<any> {
    const bulkUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.parameters.bulk;
    return this.http.post(bulkUrl, data, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get template parameter by ID
   */
  getTemplateParameter(id: number): Observable<any> {
    return this.http.get(`${this.templateParameterUrl}${id}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Create new template parameter
   */
  createTemplateParameter(data: any): Observable<any> {
    return this.http.post(this.createTemplateParameterUrl, data, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update template parameter
   */
  updateTemplateParameter(id: number, data: any): Observable<any> {
    return this.http.put(`${this.updateTemplateParameterUrl}${id}`, data, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete template parameter
   */
  deleteTemplateParameter(id: number): Observable<any> {
    return this.http.delete(`${this.deleteTemplateParameterUrl}${id}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // Utility methods
  /**
   * Get categories for template categorization
   */
  getCategories(): Observable<any> {
    return this.http.get(this.categoriesUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Generate document from smart template
   */
  generateDocument(templateId: number, parameters: any): Observable<any> {
    const generateUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.generate;
    return this.http.post(`${generateUrl}${templateId}`, { parameters }, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Preview smart template with parameters
   */
  previewTemplate(templateId: number, parameters: any = {}): Observable<any> {
    const previewUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.preview;
    return this.http.post(`${previewUrl}${templateId}`, { parameters }, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Validate template structure
   */
  validateTemplate(templateData: any): Observable<any> {
    const validateUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.validate;
    return this.http.post(validateUrl, templateData, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Export template as JSON
   */
  exportTemplate(templateId: number): Observable<any> {
    const exportUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.export;
    return this.http.get(`${exportUrl}${templateId}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Import template from JSON
   */
  importTemplate(templateData: any): Observable<any> {
    const importUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.import;
    return this.http.post(importUrl, templateData, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get template usage statistics
   */
  getTemplateStats(templateId: number): Observable<any> {
    const statsUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.stats;
    return this.http.get(`${statsUrl}${templateId}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // Template Assignment Methods
  /**
   * Get all template assignments
   */
  getTemplateAssignments(): Observable<any> {
    const assignmentsUrl = this.basePath + 'api/nomenclatoare/smart-templates/assignments/list';
    return this.http.get(assignmentsUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get template assignments for a specific document type
   */
  getTemplateAssignmentsByDocumentType(documentTypeId: number): Observable<any> {
    const assignmentsUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.smartTemplates.assignments.byDocumentType;
    return this.http.get(`${assignmentsUrl}${documentTypeId}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Save template assignments
   */
  saveTemplateAssignments(assignments: any[]): Observable<any> {
    const saveUrl = this.basePath + 'api/nomenclatoare/smart-templates/assignments/save';
    return this.http.post(saveUrl, { assignments }, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete template assignment
   */
  deleteTemplateAssignment(assignmentId: number): Observable<any> {
    const deleteUrl = this.basePath + `api/nomenclatoare/smart-templates/assignments/${assignmentId}`;
    return this.http.delete(deleteUrl, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // Document Type Methods
  /**
   * Get all document templates (for assignment purposes)
   */
  getDocumentTypes(): Observable<any> {
    const documentTemplatesUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.documentTemplate.listDT;
    return this.http.post(documentTemplatesUrl, {}, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get document types by category
   */
  getDocumentTypesByCategory(categoryId: number): Observable<any> {
    const documentTypesUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.documentTypes.byCategory;
    return this.http.get(`${documentTypesUrl}${categoryId}`, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get smart templates list for DataTable
   */
  getSmartTemplatesList(params: any): Observable<any> {
    const smartTemplatesListUrl = this.basePath + 'api/nomenclatoare/smart-templates/list-dt';
    console.log('Service making request to:', smartTemplatesListUrl, 'with params:', params);
    return this.http.post(smartTemplatesListUrl, params, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }


  /**
   * Duplicate smart template
   */
  duplicateSmartTemplate(templateId: number): Observable<any> {
    const duplicateSmartTemplateUrl = this.basePath + `api/nomenclatoare/smart-templates/${templateId}/duplicate`;
    return this.http.post(duplicateSmartTemplateUrl, {}, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Toggle smart template status
   */
  toggleSmartTemplateStatus(templateId: number): Observable<any> {
    const toggleStatusUrl = this.basePath + `api/nomenclatoare/smart-templates/${templateId}/toggle-status`;
    return this.http.patch(toggleStatusUrl, {}, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error(`Error in ${this.serviceName}:`, error);
    
    let errorMessage = 'A apărut o eroare neașteptată.';
    
    if (error.error) {
      if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.errors) {
        // Handle Laravel validation errors
        const errors = error.error.errors;
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey && errors[firstErrorKey] && errors[firstErrorKey].length > 0) {
          errorMessage = errors[firstErrorKey][0];
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return throwError(() => ({ ...error, friendlyMessage: errorMessage }));
  }
}